/**
 * Lightweight toast store — queues messages from anywhere in the app.
 * Pairs with the <ToastViewport /> component rendered in <App />.
 */
import { create } from 'zustand';

export type ToastTone = 'success' | 'error' | 'info' | 'warning';

export type Toast = {
  id: string;
  title: string;
  description?: string;
  tone: ToastTone;
  duration: number;
  /** Optional action button (e.g. "Annuler" / undo) rendered in the toast. */
  action?: {
    label: string;
    onClick: () => void;
  };
};

type ToastState = {
  toasts: Toast[];
  push: (toast: Omit<Toast, 'id' | 'duration'> & { duration?: number }) => void;
  dismiss: (id: string) => void;
};

let counter = 0;

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push: (toast) => {
    const id = `t-${++counter}`;
    const next: Toast = { id, duration: 4000, ...toast };
    set({ toasts: [...get().toasts, next] });
    if (next.duration > 0) {
      setTimeout(() => get().dismiss(id), next.duration);
    }
  },
  dismiss: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
}));

/** Convenience: fire-and-forget toast helpers. */
export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'success' }),
  error: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'error', duration: 6000 }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'info' }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'warning' }),
};
